import { forwardRef } from 'react';

import { DrawFsetupToolModeEnum } from '@spazious/dollhouse-types';
import { Hint, Typography, useHint } from '@spazious/dollhouse-uikit';
import { useEventCallback, useForkRef } from '@spazious/react-hooks';

import { useDrawFsetup } from '../../hooks/index.js';
import { ToggleButtonLabel, ToggleButtonLabelProps } from '../ToggleButtonLabel/index.js';
import { ToolActionControlPopUp, ToolActionControls } from '../ToolActionControls/index.js';

export interface ControlButtonProps extends Omit<ToggleButtonLabelProps, 'label'> {
  minWidth?: string;
  icon: React.ReactNode;
  tool: DrawFsetupToolModeEnum;
  label?: string;
  hint?: string;
  controls?: React.ReactNode;
  controlsPortal?: Element | DocumentFragment;
}

export const ControlButton = forwardRef<HTMLButtonElement, ControlButtonProps>(
  ({ icon, tool, label, hint, controls, controlsPortal, ...restProps }, fowardedRef) => {
    const { tool: selectedTool, set } = useDrawFsetup();

    const [setElement, hintProps] = useHint<HTMLButtonElement>();
    const [setReferenceElement, controlProps] = ToolActionControlPopUp<HTMLButtonElement>();
    const handleClick = useEventCallback(() => {
      set({
        tool,
        active: true,
      });
    });
    const combinedRef = useForkRef(setElement, setReferenceElement);

    return (
      <>
        <ToggleButtonLabel
          ref={useForkRef(combinedRef, fowardedRef)}
          actived={selectedTool === tool}
          onClick={handleClick}
          {...restProps}
          label={
            <Typography size="small" variant="label">
              {label}
            </Typography>
          }
        >
          {icon}
        </ToggleButtonLabel>
        {hint && selectedTool === tool && <Hint {...hintProps}>{hint}</Hint>}
        {controls && selectedTool === tool && (
          <ToolActionControls portal={controlsPortal} {...controlProps}>
            {controls}
          </ToolActionControls>
        )}
      </>
    );
  }
);
